// Paper texture asset for `pen='texture'` preset「紙」(paper, world mapping).
// (texture-brush-design.md §1 / §3 / §8 spike scope.)
//
// PARITY (texture-brush-design.md §3, ABSOLUTE): this is a **raw alpha
// array**, not a decoded PNG. The web client (Canvas2D / OffscreenCanvas)
// and the server snapshot worker (`@napi-rs/canvas` / Skia) both `import`
// this exact byte array, so there is no decoder in the loop to diverge.
//
// The grain is **generated by a fixed deterministic formula** (no
// `Math.random`) so the bytes are reproducible from the source — both
// environments run the same TS and produce the identical `Uint8Array`.
// (For the spike this is cheaper + more auditable than hand-painting a PNG
// and converting it; the §3 (a) "bake PNG → raw alpha" route stays open for
// the横展開 real artwork without changing the `TextureSampler` seam.)
//
// Visual intent: a fine, slightly directional paper tooth. Mid-to-high
// alpha everywhere (so a normal stroke still reads as solid-ish) with
// sparse lighter pits that let the下地 show through on build-up — the
// essence of「紙」world mapping (texture-brush-design.md §1 "紙").
export const PAPER_TEXTURE_SIZE = 256;
/**
 * Deterministic integer hash → grain. Pure `Math.imul` / shift / xor — all
 * integer ops, bit-identical across V8 platforms (no float, no transcendental,
 * no `Math.random`). Returns a non-negative 32-bit int.
 */
const hash2 = (x, y) => {
    let h = (Math.imul(x, 0x27d4_eb2d) ^ Math.imul(y, 0x1656_67b1)) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x85eb_ca6b) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0xc2b2_ae35) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h;
};
/**
 * Build the 256×256 paper grain alpha array (`0..255`).
 *
 * Construction (all integer arithmetic, deterministic):
 *   - a high base (≈ 220) so the brush reads as nearly solid on a single
 *     pass and reveals the tooth on build-up;
 *   - a per-texel pseudo-random dither in [-grain, +grain] from `hash2`;
 *   - a faint horizontal "tooth" ripple from a small triangle wave on `x`
 *     so the grain has a subtle direction (paper fiber feel) without any
 *     trig.
 */
const buildPaperAlpha = (size) => {
    const out = new Uint8Array(size * size);
    const base = 220;
    const grain = 30; // ± dither amplitude
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            // Triangle wave on x with period 8 → values 0..4 → 0..8 ripple.
            const tri = Math.abs(((x + ((y >> 2) & 1) * 2) % 8) - 4); // 0..4
            const ripple = tri * 2; // 0..8 darkening
            // Dither in [-grain, +grain] derived from the integer hash.
            const dither = (hash2(x, y) % (grain * 2 + 1)) - grain;
            let a = base - ripple + dither;
            if (a < 0)
                a = 0;
            if (a > 255)
                a = 255;
            out[y * size + x] = a;
        }
    }
    return out;
};
/** Cached paper grain (built once at module load; immutable thereafter). */
const PAPER_ALPHA = buildPaperAlpha(PAPER_TEXTURE_SIZE);
/**
 * The「紙」preset sampler (world mapping). Single immutable instance — every
 * texture stroke (spike) uses it.
 */
export const PAPER_TEXTURE_SAMPLER = {
    mapping: 'world',
    width: PAPER_TEXTURE_SIZE,
    height: PAPER_TEXTURE_SIZE,
    alpha: PAPER_ALPHA,
    scale: 1,
};
//# sourceMappingURL=paperTexture.js.map